import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
    subsetTotalGetter,
    SubsetTotalGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Aktivitetstyp = {
    id: string;
    namn: string;
    typ: "Träning" | "Match";
};

type State = BaseState & {
    aktivitetstyper: Map<Aktivitetstyp["id"], Aktivitetstyp>;
};

const getDefaultState: () => State = () => ({
    aktivitetstyper: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "aktivitetstyper";
const baseUrl = "skreg/aktivitetstyper/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        aktivitetstyper: (state: State) => [...state.aktivitetstyper.values()],
        aktivitetstypById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.aktivitetstyper.get(id);
            },
        aktivitetstypSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: "aktivitetstyper" }),
        // Antalet rader som finns för subsetet, det värde paginatorn räknar på
        aktivitetstypSubsetTotal: (state: State) => (props: SubsetTotalGetterProps<State>) =>
            subsetTotalGetter<State>({ ...props, state }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAktivitetstyper: (
            state: State,
            {
                offset,
                subsetName = "default",
                aktivitetstyper = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                aktivitetstyper: Aktivitetstyp[];
            }
        ) =>
            subsetSetter<State, Aktivitetstyp>({
                offset,
                subsetName,
                state,
                collectionName: "aktivitetstyper",
                items: aktivitetstyper,
                includeDefaultSubset: true,
            }),
        updateAktivitetstyp: (
            state: State,
            {
                aktivitetstyp,
                offset,
            }: {
                aktivitetstyp: Aktivitetstyp[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, Aktivitetstyp>({
                offset,
                state,
                collectionName: "aktivitetstyper",
                items: aktivitetstyp,
                includeDefaultSubset: true,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchAktivitetstyper: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Aktivitetstyp>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: "aktivitetstyper",
                    apiUrl: baseUrl,
                });

                if (results) {
                    commit("addAktivitetstyper", { subsetName, offset, aktivitetstyper: results });
                }
                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createAktivitetstyp: async (
            { commit, state }: BaseActionsProps<State>,
            { aktivitetstyp }: { aktivitetstyp: Partial<Aktivitetstyp> }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, aktivitetstyp, getConfig({}));

                commit("addAktivitetstyper", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    aktivitetstyper: [response.data],
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        updateAktivitetstyp: async (
            { commit, state }: BaseActionsProps<State>,
            { aktivitetstyp }: { aktivitetstyp: Aktivitetstyp }
        ) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${aktivitetstyp.id}/`, aktivitetstyp, getConfig({}));

                commit("updateAktivitetstyp", {
                    aktivitetstyp: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
